/**
 * InlineEdit — double-click-to-edit primitives.
 *
 * Usage:
 *   <InlineDate value={row.end_date} onSave={async (v) => updateTrial(...)} />
 *   <InlineSelect value={row.status} options={TRIAL_STATUSES} onSave={...} />
 *   <InlineText value={row.features_used} onSave={...} placeholder="—" />
 *
 * - Double-click activates the input.
 * - Enter / blur commits; Escape cancels.
 * - Shows a dotted underline on hover to hint editability.
 * - Calls onSave(newValue) — expects a Promise; shows a brief spinner.
 */

import { useState, useRef, useCallback } from 'react';

const HINT_STYLE = {
  cursor: 'default',
  borderBottom: '1px dashed transparent',
  transition: 'border-color .15s',
  display: 'inline-block',
};

const HINT_HOVER_STYLE = {
  ...HINT_STYLE,
  borderBottomColor: 'var(--c-text-3, #94a3b8)',
};

function useInlineEdit(value, onSave) {
  const [editing, setEditing]   = useState(false);
  const [draft,   setDraft]     = useState('');
  const [saving,  setSaving]    = useState(false);
  const [hovered, setHovered]   = useState(false);
  const savedRef = useRef(value);

  // Keep savedRef in sync when parent value changes (e.g. after refresh)
  if (savedRef.current !== value && !editing) {
    savedRef.current = value;
  }

  const activate = useCallback(() => {
    setDraft(savedRef.current ?? '');
    setEditing(true);
  }, []);

  const commit = useCallback(async (val) => {
    const trimmed = typeof val === 'string' ? val.trim() : val;
    setEditing(false);
    setHovered(false);
    if (trimmed === (savedRef.current ?? '')) return; // no change
    setSaving(true);
    try {
      await onSave(trimmed);
      savedRef.current = trimmed;
    } catch (e) {
      console.error('[InlineEdit] save failed', e);
    }
    setSaving(false);
  }, [onSave]);

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft(savedRef.current ?? '');
  }, []);

  return { editing, draft, setDraft, saving, hovered, setHovered, activate, commit, cancel };
}

// ── InlineText ────────────────────────────────────────────────────────────────

export function InlineText({ value, onSave, placeholder = '—', style, inputStyle }) {
  const { editing, draft, setDraft, saving, hovered, setHovered, activate, commit, cancel } = useInlineEdit(value, onSave);
  const inputRef = useRef();

  if (saving) return <span style={{ ...style, opacity: 0.5 }}>{value || placeholder}</span>;

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  { e.preventDefault(); commit(draft); }
          if (e.key === 'Escape') cancel();
        }}
        style={{
          fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 'inherit',
          color: 'inherit', background: 'var(--surface, #fff)',
          border: '1px solid var(--c-primary, #3b82f6)',
          borderRadius: 4, padding: '1px 5px', width: '100%', minWidth: 80,
          ...inputStyle,
        }}
      />
    );
  }

  return (
    <span
      style={hovered ? { ...HINT_HOVER_STYLE, ...style } : { ...HINT_STYLE, ...style }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={activate}
      title="Double-click to edit"
    >
      {value || <span style={{ color: 'var(--c-text-3, #94a3b8)' }}>{placeholder}</span>}
    </span>
  );
}

// ── InlineDate ────────────────────────────────────────────────────────────────

export function InlineDate({ value, onSave, display, style }) {
  const { editing, draft, setDraft, saving, hovered, setHovered, activate, commit, cancel } = useInlineEdit(value, onSave);

  const dateVal = (draft ?? '').slice(0, 10);

  if (saving) return <span style={{ ...style, opacity: 0.5 }}>{display || value || '—'}</span>;

  if (editing) {
    return (
      <input
        autoFocus
        type="date"
        value={dateVal}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  { e.preventDefault(); commit(draft); }
          if (e.key === 'Escape') cancel();
        }}
        style={{
          fontSize: 'inherit', fontFamily: 'inherit',
          border: '1px solid var(--c-primary, #3b82f6)',
          borderRadius: 4, padding: '1px 5px',
        }}
      />
    );
  }

  // Dates open on a SINGLE click (uniform with Trial Management's end-date
  // popover) — unlike the text inline-edits, there's no drag-to-select text
  // interaction a single click would steal.
  return (
    <span
      style={{ cursor: 'pointer', ...(hovered ? { ...HINT_HOVER_STYLE, ...style } : { ...HINT_STYLE, ...style }) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={activate}
      title="Click to edit"
    >
      {display || value || <span style={{ color: 'var(--c-text-3, #94a3b8)' }}>—</span>}
    </span>
  );
}

// ── InlineSelect ──────────────────────────────────────────────────────────────

export function InlineSelect({ value, options, onSave, display, style }) {
  // options: string[] or [{ value, label }]
  const { editing, draft, setDraft, saving, hovered, setHovered, activate, commit, cancel } = useInlineEdit(value, onSave);

  if (saving) return <span style={{ ...style, opacity: 0.5 }}>{display || value || '—'}</span>;

  if (editing) {
    return (
      <select
        autoFocus
        value={draft}
        onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
        style={{
          fontSize: 'inherit', fontFamily: 'inherit',
          border: '1px solid var(--c-primary, #3b82f6)',
          borderRadius: 4, padding: '1px 4px', background: 'var(--surface, #fff)',
        }}
      >
        {options.map((opt) => {
          const v = typeof opt === 'string' ? opt : opt.value;
          const l = typeof opt === 'string' ? opt : opt.label;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    );
  }

  return (
    <span
      style={hovered ? { ...HINT_HOVER_STYLE, ...style } : { ...HINT_STYLE, ...style }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={activate}
      title="Double-click to edit"
    >
      {display || value || <span style={{ color: 'var(--c-text-3, #94a3b8)' }}>—</span>}
    </span>
  );
}
